import {
  BreakdownItem,
  calculateInventoryBreakdown,
  defaultImage,
  formatCurrencyWithCurrencyCode,
  isProductForSale,
  StoreProduct,
  StoreProductFraction,
  getDefaultDisplayCurrencyCode,
  hasStockForUnits,
} from '@bofrak-backend/shared';
import {
  InventoryBreakdown,
  ProductDriftAlert,
  getLatestUpdatedAt,
} from '@bofrak-backend/shared-ui';
import {
  Badge,
  Box,
  Card,
  CardBody,
  Flex,
  HStack,
  IconButton,
  Image,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spacer,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useBreakpointValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { orderBy } from 'lodash';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { FaQrcode } from 'react-icons/fa6';
import { IoRefreshOutline } from 'react-icons/io5';
import { useRecoilValue } from 'recoil';
import { apiAdapter } from '@bofrak-backend/shared-ui';
import { adminAtom, merchantAtom, selectedStoreAtom } from '../recoil/atoms';
import { getPopularityScore } from '../utils/functions';

export interface LiteProductCardProps {
  product: StoreProduct;
  onOpenBarcode: (product: StoreProduct) => void;
}

const LiteProductCard: React.FC<LiteProductCardProps> = ({
  product,
  onOpenBarcode,
}) => {
  const imageSize = useBreakpointValue({ base: '60px', md: '80px' });
  const fontSize = useBreakpointValue({ base: 'xs', md: 'sm' });
  const cardPadding = useBreakpointValue({ base: 3, md: 4 });

  const merchant = useRecoilValue(merchantAtom);
  const store = useRecoilValue(selectedStoreAtom);
  const admin = useRecoilValue(adminAtom);
  const toast = useToast();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!merchant?.id || !store?.id) return;
    setIsRefreshing(true);
    try {
      await apiAdapter.getStoreProduct({
        merchant_id: merchant.id,
        store_id: store.id,
        id: product.id,
      });
      toast({ title: 'Product refreshed', status: 'success', duration: 2000 });
    } catch {
      toast({ title: 'Refresh failed', status: 'error', duration: 3000 });
    } finally {
      setIsRefreshing(false);
    }
  };

  const smallestFraction: StoreProductFraction =
    product.store_product_fractions.reduce(
      (prev, curr) => (prev.fraction < curr.fraction ? prev : curr),
      product.store_product_fractions[0],
    );

  const inventoryAmount = product.inventory ?? 0;

  // Check if product is available for sale (parent level)
  const { is_available_for_sale, reason } = isProductForSale({
    fraction: product.fraction,
    is_sold_online: product.is_sold_online,
    inventory: inventoryAmount,
    is_available_for_sale: product.is_available_for_sale,
    is_available_in_store: product.is_available_in_store,
  });

  const hasNoInventory = smallestFraction
    ? !hasStockForUnits(inventoryAmount, smallestFraction.fraction)
    : false;

  const isLowInventory =
    inventoryAmount < product.low_stock_threshold && !hasNoInventory;

  const sortedFractions = orderBy(
    product.store_product_fractions ?? [],
    'fraction',
    'desc',
  );

  // Calculate inventory breakdown for each fraction
  const inventoryBreakdown = useMemo(() => {
    const breakdown = calculateInventoryBreakdown(
      inventoryAmount,
      [product, ...product.store_product_fractions],
      product.unit,
    );
    const breakdownMap = new Map<string, number>();
    breakdown.forEach((item: BreakdownItem) => {
      breakdownMap.set(item.id, item.units);
    });
    return breakdownMap;
  }, [inventoryAmount, product]);

  const latestUpdatedAt = useMemo(() => getLatestUpdatedAt(product), [product]);
  const formattedUpdatedAt = useMemo(
    () =>
      latestUpdatedAt
        ? moment(latestUpdatedAt).format('DD.MM.YY @ HH:mm')
        : 'N/A',
    [latestUpdatedAt],
  );

  return (
    <Card
      overflow="hidden"
      boxShadow="sm"
      border={product.deleted_at ? '2px solid' : '1px solid'}
      borderColor={product.deleted_at ? 'red.500' : 'gray.200'}
      height="100%"
      bg="white"
      minH="250px"
      borderRadius="lg"
      _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
      transition="all 0.2s"
      h="full"
    >
      <CardBody p={cardPadding}>
        <Stack spacing={1} height="100%">
          {/* Header Section with Image */}
          <Flex gap={3} align="flex-start">
            {/* Product Image */}
            <Box flexShrink={0}>
              <Image
                src={product.image || defaultImage}
                alt={product.name || 'Product Image'}
                boxSize={imageSize}
                objectFit="contain"
                borderRadius="md"
                bg="gray.50"
                fallbackSrc={defaultImage}
                onError={(e) => {
                  e.currentTarget.src = defaultImage;
                }}
              />
            </Box>

            {/* Product Info */}
            <VStack align="stretch" flex={1} spacing={0}>
              <HStack justify="space-between" align="flex-start">
                <HStack
                  justify="flex-end"
                  width="full"
                  spacing={1}
                  flexShrink={0}
                >
                  {/* Fraction-drift alert */}
                  {merchant && store && admin && (
                    <ProductDriftAlert
                      product={product}
                      merchant={merchant}
                      store={store}
                      updator={{ id: admin.id, name: admin.name }}
                    />
                  )}
                  {/* Refresh button */}
                  <IconButton
                    aria-label="Refresh product"
                    icon={<IoRefreshOutline />}
                    size="xs"
                    variant="ghost"
                    onClick={handleRefresh}
                    isLoading={isRefreshing}
                    _hover={{ bg: 'gray.100' }}
                    minW="auto"
                    h="auto"
                    p={1}
                  />
                </HStack>
              </HStack>

              {/* Inventory Display */}
              <InventoryBreakdown
                inventory={inventoryAmount}
                fractions={[product, ...product.store_product_fractions]}
                label="Stock Breakdown"
                fontSize="x-small"
                fontWeight="bold"
                asButton={true}
                lowInventoryThreshold={product.low_stock_threshold}
                color={
                  !hasNoInventory && inventoryAmount > 0
                    ? inventoryAmount < product.low_stock_threshold
                      ? 'yellow.600'
                      : 'green.600'
                    : 'red.600'
                }
              />

              {/* Status Badges */}
              <VStack align="start" spacing={1}>
                {isLowInventory && !hasNoInventory && (
                  <Badge
                    colorScheme="yellow"
                    fontSize="x-small"
                    px={2}
                    py={0.1}
                    borderRadius="md"
                  >
                    Low Stock
                  </Badge>
                )}
                {hasNoInventory && (
                  <Badge
                    colorScheme="red"
                    fontSize="x-small"
                    px={2}
                    py={0.1}
                    borderRadius="md"
                  >
                    No Stock
                  </Badge>
                )}
                {product.deleted_at && (
                  <Badge
                    colorScheme="red"
                    fontSize="x-small"
                    px={2}
                    py={0.1}
                    borderRadius="md"
                  >
                    Deleted - {moment(product.deleted_at).format('DD/MM/YY')}
                  </Badge>
                )}
              </VStack>
            </VStack>
          </Flex>

          {/* Product Name */}
          <Text
            fontWeight="semibold"
            fontSize={fontSize}
            noOfLines={2}
            flex={1}
            color="gray.800"
            lineHeight="shorter"
            wordBreak="break-word"
          >
            {product.name}
          </Text>

          {/* Action Buttons — Only Barcode Manager */}
          <HStack justify="space-around">
            <Tooltip label="Barcode Manager" placement="top">
              <IconButton
                aria-label="Barcode Manager"
                icon={<FaQrcode />}
                size="xs"
                variant="ghost"
                colorScheme="teal"
                onClick={() => onOpenBarcode(product)}
              />
            </Tooltip>
          </HStack>

          {/* Fraction Table — No Cost Column */}
          <Box
            flex={1}
            w="100%"
            overflowX="auto"
            sx={{
              '&::-webkit-scrollbar': {
                height: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'gray.300',
                borderRadius: '8px',
                opacity: 0,
              },
              '&:hover::-webkit-scrollbar-thumb': {
                opacity: 1,
                transition: 'opacity 0.3s',
              },
            }}
          >
            <Table size="sm" variant="simple" minW="200px">
              <Thead>
                <Tr>
                  <Th
                    fontSize="x-small"
                    color="black"
                    borderBottom="1px solid"
                    borderColor="gray.300"
                  >
                    Name
                  </Th>
                  <Th
                    fontSize="x-small"
                    color="black"
                    borderBottom="1px solid"
                    borderColor="gray.300"
                  >
                    Stock
                  </Th>
                  <Th
                    fontSize="x-small"
                    color="black"
                    borderBottom="1px solid"
                    borderColor="gray.300"
                    isNumeric
                  >
                    Price
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {/* Main Product Row */}
                <Tr>
                  <Td py={1}>
                    <HStack overflowX="auto" spacing={1}>
                      <Popover trigger="hover" placement="left">
                        <PopoverTrigger>
                          <Box
                            w="6px"
                            h="6px"
                            borderRadius="full"
                            bg={
                              is_available_for_sale ? 'green.500' : 'red.500'
                            }
                            cursor="pointer"
                            flexShrink={0}
                          />
                        </PopoverTrigger>
                        <PopoverContent width="auto" maxW="200px">
                          <PopoverArrow />
                          <PopoverBody>
                            <Text fontSize="xs">{reason}</Text>
                          </PopoverBody>
                        </PopoverContent>
                      </Popover>
                      <Text
                        opacity={is_available_for_sale ? 1 : 0.75}
                        fontWeight="bold"
                        fontSize="x-small"
                        maxW="80px"
                      >
                        {product.unit.toString().replace(/[()]/g, '')} (
                        {product.quantity_in_unit_package})
                      </Text>
                    </HStack>
                  </Td>
                  <Td py={1} isNumeric opacity={is_available_for_sale ? 1 : 0.75}>
                    <Text fontSize="x-small">
                      {inventoryBreakdown.get(product.id) ?? 0}
                    </Text>
                  </Td>
                  <Td py={1} isNumeric opacity={is_available_for_sale ? 1 : 0.75}>
                    <Text fontWeight="medium" color="green.600" fontSize="x-small">
                      {formatCurrencyWithCurrencyCode(
                        product.price,
                        merchant?.currency.code || getDefaultDisplayCurrencyCode(),
                        true,
                      )}
                    </Text>
                  </Td>
                </Tr>

                {/* Fraction Rows */}
                {sortedFractions.map((fraction, index) => {
                  const {
                    is_available_for_sale: fracAvailable,
                    reason: fracReason,
                  } = isProductForSale({
                    fraction: fraction.fraction,
                    is_sold_online: fraction.is_sold_online,
                    inventory: fraction.inventory,
                    is_available_for_sale: fraction.is_available_for_sale,
                    is_available_in_store: fraction.is_available_in_store,
                  });

                  return (
                    <Tr key={index}>
                      <Td py={1}>
                        <HStack overflowX="auto" spacing={1}>
                          <Popover trigger="hover" placement="left">
                            <PopoverTrigger>
                              <Box
                                w="6px"
                                h="6px"
                                borderRadius="full"
                                bg={fracAvailable ? 'green.500' : 'red.500'}
                                cursor="pointer"
                                flexShrink={0}
                              />
                            </PopoverTrigger>
                            <PopoverContent width="auto" maxW="200px">
                              <PopoverArrow />
                              <PopoverBody>
                                <Text fontSize="xs">{fracReason}</Text>
                              </PopoverBody>
                            </PopoverContent>
                          </Popover>
                          <Text
                            color={
                              fraction.deleted_at ? 'red.500' : 'gray.800'
                            }
                            opacity={fracAvailable ? 1 : 0.75}
                            fontWeight="bold"
                            fontSize="x-small"
                            maxW="80px"
                          >
                            {fraction.unit
                              .toString()
                              .replace(/[()]/g, '')}{' '}
                            ({fraction.quantity_in_unit_package})
                          </Text>
                        </HStack>
                      </Td>
                      <Td py={1} isNumeric opacity={fracAvailable ? 1 : 0.75}>
                        <Text
                          color={
                            fraction.deleted_at ? 'red.500' : 'gray.700'
                          }
                          fontSize="x-small"
                        >
                          {inventoryBreakdown.get(fraction.id) ?? 0}
                        </Text>
                      </Td>
                      <Td py={1} isNumeric opacity={fracAvailable ? 1 : 0.75}>
                        <Text
                          fontWeight="medium"
                          color={
                            fraction.deleted_at ? 'red.500' : 'green.600'
                          }
                          fontSize="x-small"
                        >
                          {formatCurrencyWithCurrencyCode(
                            fraction.price,
                            merchant?.currency.code || getDefaultDisplayCurrencyCode(),
                            true,
                          )}
                        </Text>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>

          {/* Footer: Popularity + Updated At */}
          <Stack
            flexDirection={{ base: 'column', md: 'row' }}
            spacing={0}
            align="center"
            justify="space-around"
          >
            <Text fontSize="x-small" color="gray.500">
              Popularity: {getPopularityScore(product)}
            </Text>
            <Spacer />
            <Text fontSize="x-small" color="gray.500">
              {formattedUpdatedAt}
            </Text>
          </Stack>
        </Stack>
      </CardBody>
    </Card>
  );
};

export default React.memo(LiteProductCard);
